import React from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import * as Sentry from '@sentry/react'
import { Chart as ChartJS, defaults, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler, ArcElement } from 'chart.js'

import App from './components/App'
import Home from './components/Home'
import NotFound from './components/includes/NotFound'

import Login from './components/entry/Login'
import OAuthLogin from './components/entry/OAuthLogin'
import Signup from './components/entry/Signup'
import ForgottenPassword from './components/entry/ForgottenPassword'
import ResetPassword from './components/entry/ResetPassword'
import Enrol from './components/entry/Enrol'
import JoinTeam from './components/entry/JoinTeam'

import Consent from './components/Consent'
import Poll from './components/Poll'
import FileApproval from './components/FileApproval'
import Meeting from './components/Meeting'

import Setup from './components/Setup'
import Settings from './components/settings/Settings'
import OAuthConnect from './components/OAuthConnect'
import NewTrial from './components/trials/New'
import Team from './components/teams/Team'
import Trials from './components/teams/Trials'

import RootDashboard from './components/root/Dashboard'
import RootHome from './components/root/Home'
import RootEditTeam from './components/root/EditTeam'

import TrialHome from './components/trials/Home'
import TrialSettings from './components/trials/settings'
import TrialResults from './components/trials/results'
import TrialPollResponses from './components/trials/PollResponses'
import TrialInfo from './components/trials/Info'
import TrialData from './components/trials/Data'
import TrialStages from './components/trials/Stages'
import TrialNewStage from './components/trials/NewStage'
import TrialFiles from './components/trials/Files'
import TrialPersonnel from './components/trials/Personnel'
import TrialSites from './components/trials/Sites'
import TrialNewSite from './components/trials/Sites/New'
import TrialSiteMonitoring from './components/trials/Sites/Monitoring'
import TrialBooking from './components/trials/Booking'
import TrialConsent from './components/trials/Consent'
import TrialParticipants from './components/trials/Participants'
import TrialParticipant from './components/trials/Participant'
import TrialMessages from './components/trials/messages'
import TrialPacks from './components/trials/Packs'
import TrialQueries from './components/trials/Queries'
import TrialTasks from './components/trials/Tasks'

import AdminDashboard from './components/includes/AdminDashboard'
import ErrorElement from './components/includes/ErrorElement'

import { Provider } from '@/components/ui/provider'

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler, ArcElement)
defaults.maintainAspectRatio = false
defaults.responsive = true
defaults.spanGaps = true
defaults.backgroundColor = '#9BD0F5'
defaults.color = '#000'

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [],
    tracesSampleRate: 1.0
  })
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    // errorElement: <ErrorElement />,
    children: [
      { path: '', element: <Home /> },
      { path: '/setup', element: <Setup /> },
      { path: '/login', element: <Login /> },
      { path: '/login/oauth/:service', element: <OAuthLogin /> },
      { path: '/signup', element: <Signup /> },
      { path: '/password/forgotten', element: <ForgottenPassword /> },
      { path: '/password/reset', element: <ResetPassword /> },
      { path: '/enrol', element: <Enrol /> },
      { path: '/invitations/join', element: <JoinTeam /> },
      { path: 'settings', element: <Settings /> },
      { path: 'settings/:tabId', element: <Settings /> },
      { path: 'connect/:service', element: <OAuthConnect /> },
      {
        path: 'root',
        element: <RootDashboard />,
        children: [
          { path: 'teams/:teamId/edit', element: <RootEditTeam /> },
          { path: '', element: <RootHome /> }
        ]
      },
      { path: '/team', element: <Team /> },
      { path: '/team/:tabId', element: <Team /> },
      { path: '/consent/:trialIdOrCode', element: <Consent /> },
      { path: '/consent/:trialIdOrCode/:documentId', element: <Consent /> },
      { path: '/meetings/:meetingId', element: <Meeting /> },
      { path: '/q/:stageId', element: <Poll /> },
      { path: '/q/:stageId/access/:linkId', element: <Poll /> },
      { path: '/files/:fileId/approvals/:approvalId/approve', element: <FileApproval /> },
      {
        path: '/trials',
        errorElement: <ErrorElement />,
        children: [
          { path: '', element: <Trials /> },
          { path: 'new', element: <NewTrial /> },
          {
            path: ':id',
            element: <AdminDashboard />,
            children: [
              { path: '', element: <TrialHome />, errorElement: <ErrorElement /> },
              { path: 'manage', element: <TrialHome />, errorElement: <ErrorElement /> },
              { path: 'settings', element: <TrialSettings />, errorElement: <ErrorElement /> },
              { path: 'settings/:tabId', element: <TrialSettings />, errorElement: <ErrorElement /> },
              { path: 'files', element: <TrialFiles />, errorElement: <ErrorElement /> },
              { path: 'personnel', element: <TrialPersonnel />, errorElement: <ErrorElement /> },
              { path: 'personnel/:tabId', element: <TrialPersonnel />, errorElement: <ErrorElement /> },
              { path: 'poll-responses', element: <TrialPollResponses />, errorElement: <ErrorElement /> },
              { path: 'results', element: <TrialResults />, errorElement: <ErrorElement /> },
              { path: 'info', element: <TrialInfo />, errorElement: <ErrorElement /> },
              { path: 'data', element: <TrialData />, errorElement: <ErrorElement /> },
              { path: 'stages/new', element: <TrialNewStage />, errorElement: <ErrorElement /> },
              { path: 'stages/:stageId', element: <TrialNewStage />, errorElement: <ErrorElement /> },
              { path: 'stages', element: <TrialStages />, errorElement: <ErrorElement /> },
              { path: 'sites/new', element: <TrialNewSite />, errorElement: <ErrorElement /> },
              { path: 'sites/:siteId', element: <TrialNewSite />, errorElement: <ErrorElement /> },
              { path: 'sites/:siteId/monitoring', element: <TrialSiteMonitoring />, errorElement: <ErrorElement /> },
              { path: 'sites', element: <TrialSites />, errorElement: <ErrorElement /> },
              { path: 'booking', element: <TrialBooking />, errorElement: <ErrorElement /> },
              { path: 'consent', element: <TrialConsent />, errorElement: <ErrorElement /> },
              { path: 'consent/:tabId', element: <TrialConsent />, errorElement: <ErrorElement /> },
              { path: 'participants/groups', element: <TrialParticipants />, errorElement: <ErrorElement /> },
              { path: 'participants/resources', element: <TrialParticipants />, errorElement: <ErrorElement /> },
              { path: 'participants/:participantId', element: <TrialParticipant />, errorElement: <ErrorElement /> },
              { path: 'participants/:participantId/:tabId', element: <TrialParticipant />, errorElement: <ErrorElement /> },
              { path: 'participants', element: <TrialParticipants />, errorElement: <ErrorElement /> },
              { path: 'messages/:participantId', element: <TrialMessages />, errorElement: <ErrorElement /> },
              { path: 'messages', element: <TrialMessages />, errorElement: <ErrorElement /> },
              { path: 'packs', element: <TrialPacks />, errorElement: <ErrorElement /> },
              { path: 'queries', element: <TrialQueries />, errorElement: <ErrorElement /> },
              { path: 'tasks', element: <TrialTasks />, errorElement: <ErrorElement /> }
            ]
          }
        ]
      }
    ]
  },
  {
    path: '*',
    element: <NotFound />
  }
])

const root = createRoot(document.getElementById('root'))
root.render(
  <Provider>
    <RouterProvider router={router} />
  </Provider>
)
